import { z } from 'zod/v3';

/**
 * RFC-179 canonical schema: SECRET_ACCESS_GRANT.
 *
 * Vendor-agnostic representation of "principal X has Y permissions on secret
 * store Z." Drives the SOC 2 / HIPAA encryption-access compliance question:
 * "list everyone with access to encryption keys / secrets anywhere they're
 * stored." Population materialization joins this to IDENTITY_USER to render
 * the auditor-ready CSV.
 *
 * Sources: Azure Key Vault access policies + RBAC role assignments,
 * AWS KMS key policy statements, GCP Cloud KMS IAM bindings,
 * AWS Secrets Manager resource policies (future), HashiCorp Vault ACL (future).
 *
 * Honest tradeoffs:
 * - Permissions are bucketed into 5 booleans (read/write/encrypt/decrypt/manage)
 *   for cross-cloud queries. Provider-specific permission strings are preserved
 *   in `rawPermissions` for audit drill-down.
 * - AWS Deny statements + IAM conditions don't fit the boolean model. Projections
 *   evaluate effective grants where they can, set `canonicalLossy=true` and
 *   surface the rawPermissions so the auditor can verify manually.
 * - Principal display name + email require separate provider calls (AAD Graph,
 *   IAM GetUser, etc.). Projections set them to null + lossy when not synthesized.
 */

export const SECRET_ACCESS_GRANT_SCHEMA_VERSION = 1;

export const SecretAccessGrant = z.object({
  /** What kind of identity holds the grant. */
  principalType: z.enum(['user', 'group', 'service_account', 'role', 'unknown']),
  /** Provider-stable principal id (AAD objectId, AWS IAM ARN, GCP member string). */
  principalExternalId: z.string(),
  /** Human-friendly name when the projection could resolve it; null otherwise. */
  principalDisplayName: z.string().nullable(),
  /** Email when available (Okta/AAD users mostly). */
  principalEmail: z.string().email().nullable(),

  /** The secret store the grant is scoped to (Key Vault ARN, KMS key ARN, GCP key resource name). */
  vaultExternalId: z.string(),
  /** Human-friendly vault name when known. */
  vaultName: z.string().nullable(),
  /** Provider, redundant with the resource but useful in the materialized CSV. */
  provider: z.string(),

  /** Cross-cloud action buckets — coarse, designed for SOC 2 questions. */
  canReadSecrets: z.boolean(),
  canWriteSecrets: z.boolean(),
  canEncrypt: z.boolean(),
  canDecrypt: z.boolean(),
  canManageVault: z.boolean(),

  /** Provider-native permission strings preserved for audit fidelity. */
  rawPermissions: z.array(z.string()),
});

export type SecretAccessGrant = z.infer<typeof SecretAccessGrant>;
